import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import { OnboardingSurveyModal } from './ui/modals/OnboardingSurveyModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Self-contained gate that checks if the authenticated user needs to complete
 * the onboarding survey and renders the modal when needed.
 * Drop this component anywhere in the tree — it renders nothing when the
 * survey is already completed or the user is not signed in.
 */
export const OnboardingSurveyGate: React.FC = () => {
  const { user } = useAuth();
  const [showSurvey, setShowSurvey] = useState(false);
  const checkedRef = useRef(false);

  const runCheck = useCallback(async (signal?: AbortSignal) => {
    if (checkedRef.current || sessionStorage.getItem('wsle:onboarding-survey-done')) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || signal?.aborted) return;

      const res = await fetch(`${API_BASE_URL}/api/survey/onboarding`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
        signal,
      });
      if (!res.ok) return;

      const { completed } = await res.json();
      checkedRef.current = true;
      if (!completed) {
        setShowSurvey(true);
      } else {
        sessionStorage.setItem('wsle:onboarding-survey-done', '1');
      }
    } catch {
      // Silently fail (includes AbortError)
    }
  }, []);

  // Primary trigger: user state changes
  useEffect(() => {
    if (!user) return;

    const ac = new AbortController();
    runCheck(ac.signal);
    return () => ac.abort();
  }, [user, runCheck]);

  // Backup trigger: catches OAuth redirect timing gap where the [user] effect
  // may fire before the session is fully established
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => runCheck(), 500);
    };

    window.addEventListener('wallstreetle-auth-changed', handler);
    return () => {
      window.removeEventListener('wallstreetle-auth-changed', handler);
      clearTimeout(timer);
    };
  }, [runCheck]);

  if (!showSurvey) return null;

  return (
    <OnboardingSurveyModal
      isOpen={showSurvey}
      onClose={() => setShowSurvey(false)}
    />
  );
};
